import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  ChevronRight,
  CornerDownRight,
  LineChart as LineChartIcon,
  PieChart,
  Tag,
} from 'lucide-react';
import {
  formatMonthLabel,
  formatDateBR,
  monthName,
  type ReportBalancesResponse,
  type FortnightlyReportsResponse,
  type ReportDimension,
  type ReportEvolutionResponse,
  type ReportMetric,
  type ReportResponse,
  type TagsResponse,
} from '@lira/shared';
import { apiGet } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { cn } from '@/lib/cn';
import { useMonthStore } from '@/stores/month';
import { usePrivacyStore } from '@/stores/privacy';
import { CategoryIcon, EmptyState, MoneyText, Select, Skeleton } from '@/components/ui';
import { DonutChart, GroupedBarChart, LineChart } from '@/components/charts';
import { ExportCsvButton } from '@/components/ExportCsvButton';
import { PageHeader } from '@/components/layout/PageHeader';

// --- Tipos de relatório (dropdown de 5) ------------------------------------

type ReportTypeKey =
  | 'expense-category'
  | 'income-category'
  | 'expense-account'
  | 'income-account'
  | 'balances';

interface ReportTypeDef {
  label: string;
  metric: ReportMetric;
  dimension: ReportDimension;
  isBalances: boolean;
}

const REPORT_TYPES: Record<ReportTypeKey, ReportTypeDef> = {
  'expense-category': { label: 'Despesas por categoria', metric: 'expense', dimension: 'category', isBalances: false },
  'income-category': { label: 'Receitas por categoria', metric: 'income', dimension: 'category', isBalances: false },
  'expense-account': { label: 'Despesas por conta', metric: 'expense', dimension: 'account', isBalances: false },
  'income-account': { label: 'Receitas por conta', metric: 'income', dimension: 'account', isBalances: false },
  'balances': { label: 'Saldos por conta', metric: 'expense', dimension: 'account', isBalances: true },
};

const TYPE_OPTIONS = (Object.keys(REPORT_TYPES) as ReportTypeKey[]).map((value) => ({
  value,
  label: REPORT_TYPES[value].label,
}));

type ViewKey = 'donut' | 'line' | 'bar';

const VIEWS: { key: ViewKey; label: string; icon: typeof PieChart }[] = [
  { key: 'donut', label: 'Rosca', icon: PieChart },
  { key: 'line', label: 'Linha', icon: LineChartIcon },
  { key: 'bar', label: 'Barras', icon: BarChart3 },
];

/** 'YYYY-MM' → 'jul' (label curto de eixo). */
function shortMonth(ym: string): string {
  return monthName(ym).slice(0, 3);
}

// --- Toggle segmentado de visualização -------------------------------------

function ViewToggle({ value, onChange }: { value: ViewKey; onChange: (v: ViewKey) => void }) {
  return (
    <div className="inline-flex items-center gap-1 rounded-full bg-canvas p-1">
      {VIEWS.map(({ key, label, icon: Icon }) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            aria-pressed={active}
            className={cn(
              'flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              active ? 'bg-surface text-primary shadow-card' : 'text-muted hover:text-ink',
            )}
          >
            <Icon className="size-4" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

// --- Lista hierárquica (rosca) ---------------------------------------------

interface CompRow {
  id: string;
  name: string;
  color: string;
  icon: string | null;
  valueCents: number;
  percent: number;
  child?: boolean;
}

function DonutList({
  rows,
  onDrill,
}: {
  rows: CompRow[];
  onDrill: (id: string) => void;
}) {
  return (
    <ul className="flex-1 space-y-1">
      {rows.map((row) => (
        <li key={row.id}>
          <button
            type="button"
            onClick={() => onDrill(row.id)}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-canvas',
              row.child && 'pl-7',
            )}
          >
            {row.child ? (
              <CornerDownRight className="size-4 shrink-0 text-muted" />
            ) : (
              <CategoryIcon icon={row.icon} color={row.color} fallbackText={row.name} size={28} />
            )}
            <span
              className={cn(
                'min-w-0 flex-1 truncate',
                row.child ? 'text-[13px] text-muted' : 'text-sm text-ink',
              )}
            >
              {row.name}
            </span>
            <span className="shrink-0 text-xs tabular-nums text-muted">
              {Math.round(row.percent)}%
            </span>
            <MoneyText cents={row.valueCents} className="shrink-0 text-sm" />
          </button>
        </li>
      ))}
    </ul>
  );
}

// --- Visualização Rosca -----------------------------------------------------

function DonutView({
  typeKey,
  month,
  tagId,
}: {
  typeKey: ReportTypeKey;
  month: string;
  tagId: string;
}) {
  const navigate = useNavigate();
  const def = REPORT_TYPES[typeKey];

  const compositionQuery = useQuery({
    queryKey: ['report', 'composition', month, def.metric, def.dimension, tagId],
    queryFn: () => {
      const params = new URLSearchParams({
        month,
        metric: def.metric,
        dimension: def.dimension,
      });
      if (tagId) params.set('tagId', tagId);
      return apiGet<ReportResponse>(`/reports?${params.toString()}`);
    },
    enabled: !def.isBalances,
  });

  const balancesQuery = useQuery({
    queryKey: ['report', 'balances', month],
    queryFn: () => apiGet<ReportBalancesResponse>(`/reports/balances?month=${month}`),
    enabled: def.isBalances,
  });

  const isLoading = def.isBalances ? balancesQuery.isLoading : compositionQuery.isLoading;
  const isError = def.isBalances ? balancesQuery.isError : compositionQuery.isError;
  const error = def.isBalances ? balancesQuery.error : compositionQuery.error;

  // Normaliza os dois formatos numa estrutura comum (total + slices + lista).
  const model = useMemo(() => {
    if (def.isBalances) {
      const data = balancesQuery.data;
      if (!data) return null;
      const total = data.totalCurrentCents;
      const rows: CompRow[] = data.accounts.map((a) => ({
        id: a.id,
        name: a.name,
        color: a.color,
        icon: a.icon,
        valueCents: a.currentCents,
        percent: total !== 0 ? (a.currentCents / total) * 100 : 0,
      }));
      const slices = data.accounts.map((a) => ({
        id: a.id,
        label: a.name,
        value: a.currentCents,
        color: a.color,
      }));
      return { total, slices, rows, empty: data.accounts.length === 0 };
    }
    const data = compositionQuery.data;
    if (!data) return null;
    const rows: CompRow[] = [];
    for (const s of data.slices) {
      rows.push({
        id: s.id,
        name: s.name,
        color: s.color,
        icon: s.icon,
        valueCents: s.totalCents,
        percent: s.percent,
      });
      for (const c of s.children) {
        rows.push({
          id: c.id,
          name: c.name,
          color: c.color,
          icon: c.icon,
          valueCents: c.totalCents,
          percent: c.percent,
          child: true,
        });
      }
    }
    const slices = data.slices.map((s) => ({
      id: s.id,
      label: s.name,
      value: s.totalCents,
      color: s.color,
    }));
    return { total: data.totalCents, slices, rows, empty: data.slices.length === 0 };
  }, [def.isBalances, balancesQuery.data, compositionQuery.data]);

  const drill = (id: string) => {
    const params = new URLSearchParams();
    if (def.isBalances) {
      params.set('accountId', id);
    } else if (def.dimension === 'category') {
      params.set('categoryId', id);
      params.set('type', def.metric);
    } else {
      params.set('accountId', id);
      params.set('type', def.metric);
    }
    if (tagId) params.set('tagIds', tagId);
    navigate(`/transacoes?${params.toString()}`);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
        <Skeleton variant="card" className="mx-auto aspect-square w-full max-w-[220px] rounded-full sm:w-2/5" />
        <div className="flex-1 space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-9" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <p className="rounded-lg bg-expense-weak px-4 py-6 text-center text-sm text-expense">
        {errorMessage(error)}
      </p>
    );
  }

  if (!model || model.empty || model.total === 0) {
    return (
      <EmptyState
        icon={PieChart}
        title="Sem dados neste mês"
        description="Não há lançamentos para compor este relatório no mês selecionado."
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
      <div className="sm:w-2/5">
        <DonutChart
          slices={model.slices}
          centerLabel={<MoneyText cents={model.total} bold />}
          centerSub="Total"
        />
      </div>
      <DonutList rows={model.rows} onDrill={drill} />
    </div>
  );
}

// --- Visualizações Linha / Barras (evolução) --------------------------------

function EvolutionView({
  view,
  metric,
  month,
  tagId,
}: {
  view: 'line' | 'bar';
  metric: ReportMetric;
  month: string;
  tagId: string;
}) {
  const query = useQuery({
    queryKey: ['report', 'evolution', month, metric, tagId],
    queryFn: () => {
      const params = new URLSearchParams({ end: month, months: '12', metric });
      if (tagId) params.set('tagId', tagId);
      return apiGet<ReportEvolutionResponse>(`/reports/evolution?${params.toString()}`);
    },
  });

  const hasData = useMemo(
    () =>
      (query.data?.points ?? []).some(
        (p) => p.incomeCents !== 0 || p.expenseCents !== 0 || p.balanceCents !== 0,
      ),
    [query.data],
  );

  if (query.isLoading) {
    return <Skeleton variant="card" className="h-56" />;
  }

  if (query.isError) {
    return (
      <p className="rounded-lg bg-expense-weak px-4 py-6 text-center text-sm text-expense">
        {errorMessage(query.error)}
      </p>
    );
  }

  if (!query.data || !hasData) {
    return (
      <EmptyState
        icon={view === 'line' ? LineChartIcon : BarChart3}
        title="Sem histórico ainda"
        description="Registre lançamentos ao longo dos meses para ver a evolução."
      />
    );
  }

  const points = query.data.points;

  if (view === 'line') {
    return (
      <div className="mx-auto w-full max-w-2xl">
        <p className="mb-3 text-sm font-medium text-ink">Evolução do saldo</p>
        <LineChart points={points.map((p) => ({ label: shortMonth(p.month), value: p.balanceCents }))} />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-ink">Receitas x Despesas</p>
        <div className="flex items-center gap-3 text-xs text-muted">
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-full bg-income" />
            Receitas
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-full bg-expense" />
            Despesas
          </span>
        </div>
      </div>
      <GroupedBarChart
        data={points.map((p) => ({
          label: shortMonth(p.month),
          incomeCents: p.incomeCents,
          expenseCents: p.expenseCents,
        }))}
      />
    </div>
  );
}

// --- Página -----------------------------------------------------------------

function FortnightlyReportHistory() {
  const hidden = usePrivacyStore((state) => state.hidden);
  const query = useQuery({
    queryKey: ['fortnightly-reports'],
    queryFn: () => apiGet<FortnightlyReportsResponse>('/fortnightly-reports?limit=6'),
  });

  return (
    <section className="min-w-0 rounded-[var(--radius-card)] bg-surface p-5 shadow-card">
      <div>
        <h2 className="text-lg font-semibold text-ink">Resumos financeiros do Sam</h2>
        <p className="mt-1 text-sm text-muted">Primeira quinzena (1–15) e fechamento completo do mês.</p>
      </div>
      {query.isLoading ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2"><Skeleton className="h-20" /><Skeleton className="h-20" /></div>
      ) : query.isError ? (
        <p className="mt-4 text-sm text-expense">{errorMessage(query.error)}</p>
      ) : !query.data?.reports.length ? (
        <p className="mt-4 rounded-xl bg-canvas p-4 text-sm text-muted">O primeiro resumo aparecerá após o fechamento de uma quinzena.</p>
      ) : (
        <ul className="mt-4 grid min-w-0 gap-3 sm:grid-cols-2">
          {query.data.reports.map((report) => (
            <li key={report.id} className="min-w-0">
              <Link
                to={`/relatorios/quinzenal/${report.id}`}
                className="flex min-h-20 min-w-0 items-center gap-3 rounded-xl border border-border p-4 transition-colors hover:border-primary"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink">{hidden ? 'Resumo financeiro' : report.title}</p>
                  <p className="mt-1 text-xs text-muted">{formatDateBR(report.periodStart)} a {formatDateBR(report.periodEnd)}</p>
                </div>
                <ChevronRight className="size-5 shrink-0 text-muted" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function ReportsPage() {
  const month = useMonthStore((s) => s.month);
  const [view, setView] = useState<ViewKey>('donut');
  const [typeKey, setTypeKey] = useState<ReportTypeKey>('expense-category');
  const [tagId, setTagId] = useState('');

  const tagsQuery = useQuery({
    queryKey: ['tags'],
    queryFn: () => apiGet<TagsResponse>('/tags'),
  });
  const tagOptions = [
    { value: '', label: 'Todas as tags', icon: Tag },
    ...(tagsQuery.data?.tags ?? [])
      .filter((tag) => !tag.archived)
      .map((tag) => ({ value: tag.id, label: tag.name, icon: Tag })),
  ];

  const def = REPORT_TYPES[typeKey];

  return (
    <section className="space-y-5">
      <PageHeader
        title="Relatórios"
        subtitle="Entenda a composição e a evolução das suas finanças."
        meta={<span className="text-sm capitalize text-muted">{formatMonthLabel(month)}</span>}
        actions={
          <ExportCsvButton
            qs={`month=${month}${tagId ? `&tagIds=${encodeURIComponent(tagId)}` : ''}`}
            filename={`transacoes-${month}.csv`}
          />
        }
      />

      <FortnightlyReportHistory />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <ViewToggle value={view} onChange={setView} />
        <div className="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row">
          {!def.isBalances && (
            <Select<string>
              value={tagId}
              options={tagOptions}
              onChange={setTagId}
              aria-label="Filtrar relatório por tag"
              className="min-w-0 sm:w-52"
            />
          )}
          {view === 'donut' && (
            <Select<ReportTypeKey>
              value={typeKey}
              options={TYPE_OPTIONS}
              onChange={(nextType) => {
                setTypeKey(nextType);
                if (REPORT_TYPES[nextType].isBalances) setTagId('');
              }}
              aria-label="Tipo de relatório"
              className="min-w-0 sm:w-64"
            />
          )}
        </div>
      </div>

      <div className="rounded-[var(--radius-card)] bg-surface p-5 shadow-card">
        {view === 'donut' ? (
          <DonutView key={typeKey} typeKey={typeKey} month={month} tagId={tagId} />
        ) : (
          <EvolutionView view={view} metric={def.metric} month={month} tagId={tagId} />
        )}
      </div>
    </section>
  );
}
